import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logError } from "@/lib/log";

// Guides the signed-in member marked "This helped" (newest first)
export const useHelpedGuides = () => {
  const { user } = useAuth();
  const enabled = !!user?.has_paid;
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!enabled) return setList([]);
    api.get("/guides/helped").then((r) => setList(r.data)).catch((e) => { logError("Helped guides failed:", e); setList([]); });
  }, [enabled]);

  const isHelped = useCallback((slug) => !!list?.some((g) => g.slug === slug), [list]);

  const toggle = async (slug, title) => {
    const on = isHelped(slug);
    const prev = list;
    setList((l) => (on ? l.filter((g) => g.slug !== slug) : [{ slug, at: new Date().toISOString(), note: "" }, ...l]));
    try {
      await api.post(on ? "/guides/helped/remove" : "/guides/helped", on ? { slug } : { slug, title });
      toast.success(on ? "Removed from guides you've read" : "Saved to guides you've read 💜");
    } catch (e) { setList(prev); toast.error(errMsg(e)); }
  };

  const noteOf = useCallback((slug) => list?.find((g) => g.slug === slug)?.note || "", [list]);

  const saveNote = async (slug, note) => {
    try {
      const { data } = await api.put("/guides/helped/note", { slug, note });
      setList((l) => l.map((g) => (g.slug === slug ? { ...g, note: data.note } : g)));
      toast.success(data.note ? "Note saved" : "Note removed");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const clear = async () => {
    const prev = list;
    setList([]);
    try { await api.delete("/guides/helped"); toast.success("List cleared"); }
    catch (e) { setList(prev); toast.error(errMsg(e)); }
  };

  return { enabled, list, isHelped, toggle, clear, noteOf, saveNote };
};
